import { useState, useMemo, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Trash2, Link as LinkIcon, Pencil, ExternalLink } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/useToast';
import { useDeleteSiteLinkMutationOptions, useUpdateSiteLinkMutationOptions } from '@/lib/query-options';
import type { CampaignData, SiteLink } from '@/lib/types';
import { useRefetchCampaign } from '@/hooks/useRefetchCampaign';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { DataTable } from '@/components/glitch-ui/data-table';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { CharacterCounter } from '@/components/ui/character-counter';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';
import { BusinessLandingPageCombobox } from '@/components/glitch-ui/business-landing-page-combobox';

interface SiteLinksSectionProps {
  campaign: CampaignData;
  disabled?: boolean;
}

export interface SitelinkSectionRef {
  openAdd: () => void;
}

// Unified row: the campaign's own sitelinks (editable) plus account-level sitelinks inherited
// by the campaign (read-only — managed on the account, no per-campaign opt-out).
type SitelinkRow = {
  key: string;
  name: string;
  url: string;
  description1: string;
  description2: string;
  source: 'account' | 'campaign';
  original_campaign?: SiteLink;
};

// #5851: case- and whitespace-insensitive key for duplicate-name detection.
const normalizeSitelinkName = (name: string) => name.toLowerCase().replace(/\s+/g, ' ').trim();

export const SitelinkSection = forwardRef<SitelinkSectionRef, SiteLinksSectionProps>(({ campaign, disabled = false }, ref) => {
  const { id: campaignId } = campaign;
  const refetchCampaign = useRefetchCampaign(campaign.slug);
  const { toast } = useToast();

  const [deletingSiteLink, setDeletingSiteLink] = useState<SiteLink | null>(null);
  const [newSiteLinkId, setNewSiteLinkId] = useState<number | null>(null);

  // Edit/Add modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSiteLink, setEditingSiteLink] = useState<SiteLink | null>(null);
  const [modalName, setModalName] = useState('');
  const [modalUrl, setModalUrl] = useState('');
  const [modalDesc1, setModalDesc1] = useState('');
  const [modalDesc2, setModalDesc2] = useState('');
  // Tell-on-click (#5857): Save stays enabled; clicking marks missing/invalid fields.
  type SitelinkFieldError = Partial<Record<'name' | 'url' | 'descPair', string>>;
  const [fieldErrors, setFieldErrors] = useState<SitelinkFieldError>({});

  const siteLinkUpdateMutation = useUpdateSiteLinkMutationOptions();
  const siteLinkDeleteMutation = useDeleteSiteLinkMutationOptions();

  const localSiteLinks = campaign.site_links || [];
  const accountSiteLinks = campaign.account_sitelinks || [];

  // Campaign sitelinks first (newest at top), then inherited account sitelinks.
  const rows: SitelinkRow[] = useMemo(() => [
    ...[...localSiteLinks].reverse().map((s, i): SitelinkRow => ({
      key: s.id ? `campaign-${s.id}` : `new-${i}`,
      name: s.name,
      url: s.url,
      description1: s.description1 || '',
      description2: s.description2 || '',
      source: 'campaign',
      original_campaign: s,
    })),
    ...accountSiteLinks.map((s): SitelinkRow => ({
      key: `account-${s.customer_asset_resource_name}`,
      name: s.name,
      url: s.url,
      description1: s.description1 || '',
      description2: s.description2 || '',
      source: 'account',
    })),
  ], [localSiteLinks, accountSiteLinks]);

  // Clear highlight after 3 seconds
  useEffect(() => {
    if (!newSiteLinkId) return;
    const timer = setTimeout(() => setNewSiteLinkId(null), 3000);
    return () => clearTimeout(timer);
  }, [newSiteLinkId]);

  const openAddModal = () => {
    setEditingSiteLink(null);
    setModalName('');
    setModalUrl('');
    setModalDesc1('');
    setModalDesc2('');
    setFieldErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = useCallback((siteLink: SiteLink) => {
    setEditingSiteLink(siteLink);
    setModalName(siteLink.name || '');
    setModalUrl(siteLink.url || '');
    setModalDesc1(siteLink.description1 || '');
    setModalDesc2(siteLink.description2 || '');
    setFieldErrors({});
    setIsModalOpen(true);
  }, []);

  const isValidUrl = (url: string): boolean => {
    try {
      const u = new URL(url);
      if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
      // Reject single-label hostnames like ``test`` or ``localhost`` (#4459). ``new URL``
      // accepts them as valid per WHATWG, but Google Ads rejects them at publish time —
      // require a dotted hostname with a 2+ char TLD.
      return /\.[a-z]{2,}$/i.test(u.hostname);
    } catch {
      return false;
    }
  };

  const handleSave = async () => {
    let normalizedUrl = modalUrl.trim();
    if (normalizedUrl && !/^https?:\/\//i.test(normalizedUrl)) {
      normalizedUrl = `https://${normalizedUrl}`;
    }

    const errors: SitelinkFieldError = {};
    if (!modalName.trim()) {
      errors.name = 'Required';
    } else {
      // #5851: block a duplicate sitelink name (would otherwise create a redundant Google asset).
      // Compare against the campaign's own sitelinks plus inherited account sitelinks, excluding the
      // row being edited so re-saving an unchanged name is allowed. Surfaced via the tell-on-click
      // ``fieldErrors.name`` path (#5857) rather than a separate error state.
      const nameKey = normalizeSitelinkName(modalName);
      const duplicateName = [
        ...localSiteLinks.filter((s) => !editingSiteLink || s.id !== editingSiteLink.id),
        ...accountSiteLinks,
      ].some((s) => normalizeSitelinkName(s.name) === nameKey);
      if (duplicateName) errors.name = 'A sitelink with this name already exists on this campaign.';
    }
    if (!normalizedUrl) errors.url = 'Required';
    else if (!isValidUrl(normalizedUrl)) errors.url = 'Enter a valid URL';
    if (Boolean(modalDesc1.trim()) !== Boolean(modalDesc2.trim())) errors.descPair = 'Provide both or neither';
    if (Object.values(errors).some(Boolean)) {
      setFieldErrors(errors);
      return;
    }

    try {
      const siteLink: SiteLink = {
        ...(editingSiteLink || {}),
        name: modalName.trim(),
        url: normalizedUrl,
        description1: modalDesc1.trim(),
        description2: modalDesc2.trim(),
      };

      const result = await siteLinkUpdateMutation.mutateAsync({ campaignId, siteLink });
      setIsModalOpen(false);
      await refetchCampaign();

      // Highlight newly created sitelink using the API response ID
      if (!editingSiteLink && result?.id) {
        setNewSiteLinkId(result.id);
      }
    } catch (error) {
      console.error(error);
      toast({
        description: error instanceof Error ? error.message : 'Failed to save sitelink',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingSiteLink?.id) return;
    try {
      await siteLinkDeleteMutation.mutateAsync({ campaignId, siteLinkId: deletingSiteLink.id });
      setDeletingSiteLink(null);
      refetchCampaign();
    } catch (error) {
      console.error(error);
      toast({
        description: error instanceof Error ? error.message : 'Failed to delete sitelink',
        variant: 'destructive',
      });
    }
  };

  const columns: ColumnDef<SitelinkRow, unknown>[] = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Sitelink',
      cell: ({ row }) => (
        <div className='flex items-center gap-2'>
          <LinkIcon className='h-3.5 w-3.5 text-gray-400 shrink-0' />
          <div>
            <div className='text-sm font-medium text-gray-900'>{row.original.name}</div>
            <div className='text-xs text-gray-500'>{row.original.url}</div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'description1',
      header: 'Description',
      cell: ({ row }) => (
        <div>
          <div className='text-sm text-gray-900'>{row.original.description1 || '-'}</div>
          <div className='text-sm text-gray-900'>{row.original.description2 || ''}</div>
        </div>
      ),
    },
    {
      accessorKey: 'source',
      header: 'Source',
      cell: ({ row }) => (
        row.original.source === 'account' ? (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600'>
            Account
          </span>
        ) : (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-600'>
            Campaign
          </span>
        )
      ),
    },
    {
      id: 'actions',
      header: () => null,
      enableSorting: false,
      cell: ({ row }) => {
        // Account sitelinks are inherited and read-only here — manage them on the account page.
        if (row.original.source === 'account') {
          return (
            <div className='flex justify-end'>
              <Link
                to="/assets"
                hash="google"
                className='text-sm text-[#290BD9] hover:text-[#2309C5] inline-flex items-center gap-1'
              >
                Manage
                <ExternalLink className='h-3.5 w-3.5' />
              </Link>
            </div>
          );
        }

        const siteLink = row.original.original_campaign;
        if (!siteLink || disabled) return null;
        return (
          <div className='flex justify-end gap-1'>
            <IconButton variant='default' onClick={() => openEditModal(siteLink)}>
              <Pencil className='h-4 w-4' />
            </IconButton>
            <IconButton variant='destructive' onClick={() => setDeletingSiteLink(siteLink)}>
              <Trash2 className='h-4 w-4' />
            </IconButton>
          </div>
        );
      },
    },
  ], [openEditModal, disabled]);

  useImperativeHandle(ref, () => ({ openAdd: openAddModal }), [openAddModal]);

  return (
    <div>
      {/* Table — read-only rows */}
      <DataTable<SitelinkRow>
        data={rows}
        columns={columns}
        pageSize={999}
        emptyIcon={LinkIcon}
        emptyTitle='No Sitelinks'
        emptyDescription='Add links to help users navigate to specific pages on your website.'
        getRowClassName={(row) => row.original_campaign?.id === newSiteLinkId ? 'ring-1 ring-green-400 bg-green-50/50' : undefined}
      />

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className='sm:max-w-[500px] max-h-[90vh] overflow-y-auto' aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{editingSiteLink ? 'Edit Sitelink' : 'Add Sitelink'}</DialogTitle>
          </DialogHeader>
          <div className='space-y-4 py-2'>
            <div>
              <div className='flex items-center justify-between'>
                <Label>Name</Label>
                <FieldError message={fieldErrors.name} />
              </div>
              <div className='relative mt-1'>
                <Input
                  value={modalName}
                  onChange={(e) => { setModalName(e.target.value); setFieldErrors(prev => ({ ...prev, name: undefined })); }}
                  maxLength={25}
                  placeholder='e.g. Contact Us'
                  autoFocus
                  error={!!fieldErrors.name}
                  className={fieldErrors.name ? 'pr-20' : 'pr-14'}
                />
                <InputAdornment error={!!fieldErrors.name}>
                  <CharacterCounter current={modalName.length} max={25} variant='compact' />
                </InputAdornment>
              </div>
            </div>
            <div>
              <div className='flex items-center justify-between'>
                <Label>URL</Label>
                <FieldError message={fieldErrors.url} />
              </div>
              {/* Same URL picker as the account sitelink modal (#5855): entries from the
                  business's landing pages, typed URLs validated + persisted to /assets. */}
              <div className={fieldErrors.url ? 'relative mt-1 rounded-md ring-1 ring-red-500' : 'relative mt-1'}>
                <BusinessLandingPageCombobox
                  selectedUrl={modalUrl || null}
                  onSelect={(entry) => { setModalUrl(entry?.url ?? ''); setFieldErrors(prev => ({ ...prev, url: undefined })); }}
                  placeholder='Select or add a landing page URL...'
                />
                <InputAdornment error={!!fieldErrors.url} />
              </div>
            </div>
            <div>
              <div className='flex items-center justify-between'>
                <Label>Description Line 1</Label>
                {!modalDesc1.trim() && <FieldError message={fieldErrors.descPair} />}
              </div>
              <div className='relative mt-1'>
                <Input
                  value={modalDesc1}
                  onChange={(e) => { setModalDesc1(e.target.value); setFieldErrors(prev => ({ ...prev, descPair: undefined })); }}
                  maxLength={35}
                  placeholder='Optional (must be paired with line 2)'
                  error={!!fieldErrors.descPair && !modalDesc1.trim()}
                  className={fieldErrors.descPair && !modalDesc1.trim() ? 'pr-20' : 'pr-14'}
                />
                <InputAdornment error={!!fieldErrors.descPair && !modalDesc1.trim()}>
                  <CharacterCounter current={modalDesc1.length} max={35} variant='compact' />
                </InputAdornment>
              </div>
            </div>
            <div>
              <div className='flex items-center justify-between'>
                <Label>Description Line 2</Label>
                {!modalDesc2.trim() && <FieldError message={fieldErrors.descPair} />}
              </div>
              <div className='relative mt-1'>
                <Input
                  value={modalDesc2}
                  onChange={(e) => { setModalDesc2(e.target.value); setFieldErrors(prev => ({ ...prev, descPair: undefined })); }}
                  maxLength={35}
                  placeholder='Optional (must be paired with line 1)'
                  error={!!fieldErrors.descPair && !modalDesc2.trim()}
                  className={fieldErrors.descPair && !modalDesc2.trim() ? 'pr-20' : 'pr-14'}
                />
                <InputAdornment error={!!fieldErrors.descPair && !modalDesc2.trim()}>
                  <CharacterCounter current={modalDesc2.length} max={35} variant='compact' />
                </InputAdornment>
              </div>
            </div>
            {Boolean(modalDesc1.trim()) !== Boolean(modalDesc2.trim()) && (
              <p className='text-xs text-red-500'>
                Provide both description lines or leave both blank. Google Ads requires them as a pair.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setIsModalOpen(false)} className='leading-none'>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={siteLinkUpdateMutation.isPending}
              className='bg-[#290BD9] hover:bg-[#2309C5] leading-none'
            >
              {siteLinkUpdateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConformationModal
        isOpen={!!deletingSiteLink}
        onClose={() => setDeletingSiteLink(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Sitelink"
        description={`Are you sure you want to delete "${deletingSiteLink?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={siteLinkDeleteMutation.isPending}
      />
    </div>
  );
});
SitelinkSection.displayName = 'SitelinkSection';
